/* eslint-disable max-len */
import {
  Show,
  ReferenceField,
  TextField,
  SimpleShowLayout,
  useTranslate,
  NumberField,
  FunctionField,
  Labeled,
  useShowContext,
  BooleanField
} from "react-admin";
import { useQuery } from "@apollo/client";
import CustomHeaderActions from "./CustomHeaderActions";
import ShowAndEditDetails from "./ShowAndEditDetails";
import * as Axios from "../providers/axios";
import * as User from "../providers/user";
import { CompanyDetailsIcon, EditButtonIcon } from "assets/Icons";
import Gallery from "./Gallery";
import GalleryImage from "./GalleryImage";
import { useParams } from "react-router-dom";
import AdvancedDetails from "./AdvancedDetails";
import { Box, Chip } from "@mui/material";
import { amenitiesNameHelper } from "utils/helpers";
import { CustomButton } from "./CustomButton";
import { Link } from "react-router-dom";
import ShowCards from "./ShowCards";
import { GET_COMPANY_PAYMENT_METHOD } from "../gql";

const LoginAsOwnerButton = () => {
  const { api } = Axios.useAxios();
  const { permissions } = User.useUser();
  const { id: idFromParams } = useParams<{ id: string }>();
  const id = idFromParams || permissions?.companyId;
  const translate = useTranslate();

  const handleImpersonate = async () => {
    localStorage.removeItem("ServiceCalendarLocationFilter");
    localStorage.removeItem("ServiceCalendarDateFilter");
    localStorage.removeItem("RaStore.Availability.listParams");
    try {
      await api.post("v1/companies/impersonate-login", {
        companyId: id
      });

      window.location.reload();
    } catch (error) {
      throw new Error("Error impersonating owner: " + error.message);
    }
  };

  return (
    <CustomButton
      onClick={handleImpersonate}
      variant="secondary"
      text={translate("companies.login_as_owner")}
      sx={{
        "@media screen and (max-width:640px)": {
          width: "100%"
        }
      }}
    />
  );
};

const ConditionalTextField = ({
  sourceName
}: {
  sourceName: string;
  label?: any;
}) => {
  const { record } = useShowContext();
  const translate = useTranslate();

  if (sourceName === "default_service_id" && record?.default_service_id) {
    return (
      <ReferenceField
        source="default_service_id"
        reference="Service"
        label={translate("companies.default_service")}
      >
        <Labeled label={translate("companies.default_service")}>
          <TextField source="name" />
        </Labeled>
      </ReferenceField>
    );
  }

  if (sourceName === "amenities_enums") {
    if (record?.amenities_enums?.length > 0) {
      return (
        <>
          <p className="text-[14px] text-mono-secondaryTextColor font-['Inter']">
            {translate("companies.amenities")}
          </p>
          <div className="flex gap-2 flex-wrap">
            {record?.amenities_enums.map((item: string, index: number) => (
              <Chip
                key={index}
                label={
                  amenitiesNameHelper[item as keyof typeof amenitiesNameHelper]
                }
              />
            ))}
          </div>
        </>
      );
    } else {
      return null;
    }
  }

  if (record?.[sourceName]) {
    return (
      <Labeled label={translate(`companies.${sourceName}`)}>
        <TextField
          source={sourceName}
          sx={{
            wordBreak: "break-all"
          }}
        />
      </Labeled>
    );
  }

  return null;
};

const PercentageField = ({ sourceName }: { sourceName: string }) => {
  const { record } = useShowContext();
  const translate = useTranslate();

  if (typeof record?.[sourceName] === "number") {
    return (
      <Labeled label={translate(`companies.${sourceName}`)}>
        <FunctionField
          source={sourceName}
          render={(record: any) =>
            `${parseFloat((record[sourceName] * 100).toFixed(2))}%`
          }
        />
      </Labeled>
    );
  }
  return null;
};

const CompanyDetailsOnCustomerButton = () => {
  const { record } = useShowContext();
  const translate = useTranslate();

  if (record?.slug) {
    return (
      <Link
        to={`${process.env.REACT_APP_CUSTOMER_APP_URL}/company/${record.slug}`}
        style={{ width: "inherit" }}
        target="_blank"
      >
        <CustomButton
          variant="secondary"
          text={translate("companies.view_company_page")}
          sx={{
            "@media screen and (max-width:640px)": {
              width: "100%"
            }
          }}
        />
      </Link>
    );
  }
  return null;
};

const CompanyPlan = () => {
  const { record } = useShowContext();
  const translate = useTranslate();

  return (
    <div className="w-full mb-4">
      <div className="w-full flex-wrap flex items-center justify-between gap-4">
        <div className="flex items-center w-full sm:w-auto justify-between gap-5">
          <div className="text-[16px] leading-10 text-[rgba(48,47,46,0.95)] font-[400] text-left">
            {translate("companies.selected_plan")}
            {": "}
            <span
              className="text-[rgba(184,114,42,1)] text-[28px] font-[600] font-['Famous']"
              style={{ letterSpacing: "4px" }}
            >
              {record?.status}
            </span>
          </div>
        </div>
        <div className="flex items-center w-full sm:w-auto justify-between gap-2">
          {record?.current_period_end_at && (
            <div className="text-[14px] text-[rgba(48,47,46,0.8)] mr-2">
              {translate("companies.next_payment_on")}{" "}
              {new Date(record.current_period_end_at).toLocaleDateString()}
            </div>
          )}
          {record?.cancel_at_period_end_at && (
            <div className="text-[14px] text-[rgba(48,47,46,0.8)] mr-2">
              {translate("companies.active_until")}{" "}
              {new Date(record.cancel_at_period_end_at).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function CompanyShow() {
  const translate = useTranslate();
  const { user, permissions } = User.useUser();
  const { id: idFromParams } = useParams<{ id: string }>();
  const id = idFromParams || permissions?.companyId;

  const { data: companyData, refetch } = useQuery(GET_COMPANY_PAYMENT_METHOD, {
    variables: {
      where: { id }
    }
  });
  if (!companyData) {
    return null;
  }

  return (
    <Show
      actions={
        <CustomHeaderActions
          title={translate("companies.company")}
          hideBackButton={!idFromParams}
        />
      }
      resource="Company"
      id={id}
    >
      <SimpleShowLayout spacing={0}>
        <ShowAndEditDetails
          title={translate("companies.company_details")}
          view="show"
          icon={<CompanyDetailsIcon />}
          marginBottom="1px"
          customButtons={
            <div className="flex items-center gap-3 sm:gap-2 flex-wrap w-full sm:w-auto">
              {permissions?.isSuperAdmin &&
                !user.impersonate_company_id &&
                id !== permissions.companyId && <LoginAsOwnerButton />}
              <CompanyDetailsOnCustomerButton />
              <Link
                to={`/dashboard/Company/${id}`}
                style={{ width: "inherit" }}
              >
                <CustomButton
                  text={translate("common.edit")}
                  guideId="edit-company-button"
                  variant="secondary"
                  startIcon={<EditButtonIcon />}
                  sx={{
                    "@media screen and (max-width:640px)": {
                      width: "100%"
                    }
                  }}
                />
              </Link>
            </div>
          }
        />
        <CompanyPlan />
        <Gallery>
          <GalleryImage
            height="250px"
            width="745px"
            source="cover_photo_url"
            empty="Cover image"
            logoSource="logo_url"
          />
        </Gallery>
        <Box className="line-clamp-1 break-all">
          <ReferenceField
            source="owner_id"
            reference="User"
            label={translate("companies.owner")}
          />
        </Box>
        <ConditionalTextField sourceName="contact_email" />
        <TextField source="name" label={translate("companies.name")} />
        {permissions?.isSuperAdmin && (
          <BooleanField
            source="is_verified"
            label={translate("companies.is_verified")}
          />
        )}
        {permissions.isSuperAdmin && (
          <FunctionField
            source="status"
            label={translate("companies.status")}
            render={({ status }: { status: string }) => {
              return translate(`ra.choices.${status}`);
            }}
          />
        )}
        {(!permissions?.isCompanyBasic || permissions?.isSuperAdmin) && (
          <FunctionField
            source="payment_type"
            label={translate("companies.payment_type")}
            render={({ payment_type }: { payment_type: string }) => {
              return translate(`ra.choices.${payment_type}`);
            }}
          />
        )}
        {/*TODO investigation of unnecessary spacing added by SimpleShowLayout to empty fields */}
        {/*by label={false} this fields will still have labels but unnecessary spacing will be removed */}
        {/*I am not even using this prop in component it so its important to write label={false} on every single one of them */}
        <ConditionalTextField label={false} sourceName="caption" />
        <ConditionalTextField label={false} sourceName="about" />
        <ConditionalTextField label={false} sourceName="amenities_enums" />
        <ConditionalTextField label={false} sourceName="facebook_url" />
        <ConditionalTextField label={false} sourceName="instagram_url" />
        <ConditionalTextField label={false} sourceName="twitter_url" />
        <ConditionalTextField label={false} sourceName="website_url" />
        <TextField
          source="address_display"
          label={translate("companies.billing_address")}
        />
        <ShowCards company={companyData?.company} refetch={refetch} />
        {!permissions?.isCompanyLimited && !permissions?.isCompanyBasic && (
          <AdvancedDetails marginTop={8}>
            <SimpleShowLayout spacing={0}>
              <ConditionalTextField
                label={false}
                sourceName="default_service_id"
              />
              <TextField
                source="user_pays_fees"
                label={translate("companies.user_pays_fees")}
              />
              <NumberField
                source="timeslot_spacing"
                label={translate("companies.timeslot_spacing")}
              />
              <NumberField
                source="late_cancellation_hours"
                label={translate("companies.late_cancellation_hours")}
              />
              <PercentageField sourceName="late_cancellation_charge" />
              <PercentageField sourceName="no_show_charge" />
              <PercentageField sourceName="retail_tax_rate" />
            </SimpleShowLayout>
          </AdvancedDetails>
        )}
      </SimpleShowLayout>
    </Show>
  );
}
